import isEqual from 'lodash-es/isEqual';
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  message
} from 'antd';
import { ShowFieldData } from 'components/ShowFieldData';
import { IAddress, ICity, IUser } from 'interfaces/users';
import { IState } from 'interfaces/address';
import { InputMask } from 'components/form-items/InputMask';
import {
  buildUserAddressPayload,
  cepRule,
  isAddressPayloadValid,
  requiredAddressTextRule,
  requiredCityRule
} from './addressFormUtils';
import { fetchAddressByCep } from 'services/address';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useList, useUpdate } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';

interface UserEditAddressSectionProps {
  record: IUser;
  /** Increments when profile save succeeds; clears local address override only then. */
  profileSaveNonce: number;
}

// eslint-disable-next-line sonarjs/cognitive-complexity -- CEP + state/city lookup wiring
export function UserEditAddressSection({
  record,
  profileSaveNonce
}: UserEditAddressSectionProps) {
  const [addressOverride, setAddressOverride] = useState<IAddress | null>(null);
  const [addressCompareTick, setAddressCompareTick] = useState(0);
  /** Payload equality baseline when entering address edit (after sync). */
  const addressBaselinePayloadRef = useRef<Record<string, unknown> | null>(
    null
  );

  const effectiveRecord = useMemo<IUser>(() => {
    if (!addressOverride) {
      return record;
    }

    return { ...record, address: addressOverride };
  }, [record, addressOverride]);

  useEffect(() => {
    if (profileSaveNonce === 0) {
      return;
    }

    setAddressOverride(null);
  }, [profileSaveNonce]);

  const { mutate: saveAddress, isLoading: addressSaving } = useUpdate();

  const [addressForm] = Form.useForm<Record<string, unknown>>();

  const [addressEditing, setAddressEditing] = useState(false);

  const [selectedStateId, setSelectedStateId] = useState<string | undefined>();
  const [pendingCityIbge, setPendingCityIbge] = useState<string | undefined>();
  const [cepCityOption, setCepCityOption] = useState<
    { label: string; value: string } | undefined
  >();

  const lastFetchedCep = useRef('');
  const [cityLookupKey, setCityLookupKey] = useState(0);

  useEffect(() => {
    if (effectiveRecord.address?.city?.state_id) {
      setSelectedStateId(effectiveRecord.address.city.state_id);
    }

    if (effectiveRecord.address?.city) {
      setCepCityOption({
        label: effectiveRecord.address.city.name,
        value: effectiveRecord.address.city.id
      });
    }
  }, [effectiveRecord.address?.city]);

  const { selectProps: stateSelectProps } = useSelect<IState>({
    resource: 'states',
    optionLabel: 'uf',
    optionValue: 'id',
    fetchSize: 100,
    liveMode: 'manual',
    pagination: { mode: 'server' }
  });

  const { selectProps: citySelectProps } = useSelect<ICity>({
    resource: 'cities',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 50,

    onSearch: value => [
      {
        field: 'filter[by_name]',
        operator: 'eq',
        value
      }
    ],

    filters: selectedStateId
      ? [
          {
            field: 'filter[by_state_id]',
            operator: 'eq',
            value: selectedStateId
          }
        ]
      : [
          {
            field: 'filter[by_id]',
            operator: 'eq',
            value: effectiveRecord.address?.city?.id
          }
        ],

    pagination: { mode: 'server' },

    queryOptions: {
      queryKey: [
        'cities',
        selectedStateId ?? effectiveRecord.address?.city?.id ?? 'none'
      ]
    }
  });

  const { data: cepCityResult, isFetching: isCityFetching } = useList<ICity>({
    resource: 'cities',
    filters: pendingCityIbge
      ? [
          {
            field: 'filter[by_ibge_code]',
            operator: 'eq',
            value: pendingCityIbge
          }
        ]
      : [],
    pagination: { pageSize: 1, mode: 'server' },
    queryOptions: {
      enabled: !!pendingCityIbge,
      queryKey: ['cep-city', pendingCityIbge, cityLookupKey]
    }
  });

  useEffect(() => {
    if (isCityFetching || !pendingCityIbge) {
      return;
    }

    const city = cepCityResult?.data?.[0];

    if (!city) {
      message.warning(
        'Não localizamos a cidade do CEP na base. Selecione manualmente.'
      );
      // eslint-disable-next-line unicorn/no-useless-undefined
      setPendingCityIbge(undefined);
      return;
    }

    addressForm.setFieldValue('city_id', city.id);
    setCepCityOption({ label: city.name, value: city.id });
    // eslint-disable-next-line unicorn/no-useless-undefined
    setPendingCityIbge(undefined);
    setAddressCompareTick(t => t + 1);
  }, [cepCityResult, addressForm, pendingCityIbge, isCityFetching]);

  const stateOptionsRef = useRef(stateSelectProps.options);
  stateOptionsRef.current = stateSelectProps.options;

  const addressFormRef = useRef(addressForm);
  addressFormRef.current = addressForm;

  const cepValue = Form.useWatch('postal_code', addressForm);

  // eslint-disable-next-line sonarjs/cognitive-complexity -- ViaCEP result wiring
  useEffect(() => {
    if (!addressEditing) {
      return;
    }

    const cep = (cepValue ?? '').replace(/\D/g, '');

    if (cep.length !== 8 || cep === lastFetchedCep.current) {
      return;
    }

    lastFetchedCep.current = cep;

    fetchAddressByCep(cep)
      .then(address => {
        if (lastFetchedCep.current !== cep) {
          return;
        }

        if (!address) {
          message.warning(
            'CEP não encontrado. Confira os dígitos ou preencha o endereço manualmente.'
          );
          return;
        }

        const form = addressFormRef.current;

        const addressFields: Record<string, string> = {};

        if (address.street) {
          addressFields.street = address.street;
        }

        if (address.neighborhood) {
          addressFields.neighborhood = address.neighborhood;
        }

        if (Object.keys(addressFields).length > 0) {
          form?.setFieldsValue(addressFields);
        }

        const normalizedUf = String(address.state ?? '')
          .trim()
          .toUpperCase();
        const stateOption = stateOptionsRef.current?.find(
          opt => String(opt.label).trim().toUpperCase() === normalizedUf
        );

        if (stateOption) {
          const stateId = stateOption.value as string;
          form?.setFieldValue('state_id', stateId);
          // eslint-disable-next-line unicorn/no-useless-undefined
          form?.setFieldValue('city_id', undefined);
          setSelectedStateId(stateId);
          setPendingCityIbge(address.ibgeCode);
          setCityLookupKey(k => k + 1);
        } else {
          message.warning(
            'Não foi possível identificar o estado do CEP. Selecione estado e cidade manualmente.'
          );
        }

        setAddressCompareTick(t => t + 1);
      })
      .catch(() => {
        message.error(
          'Não foi possível buscar o endereço pelo CEP. Verifique a conexão ou preencha manualmente.'
        );
      });
  }, [cepValue, addressEditing]);

  const stateUfLabel = useMemo(() => {
    const sid = effectiveRecord.address?.city?.state_id;

    if (!sid || !stateSelectProps.options?.length) {
      return;
    }

    const opt = stateSelectProps.options.find(o => o.value === sid);
    return opt?.label as string | undefined;
  }, [effectiveRecord.address?.city?.state_id, stateSelectProps.options]);

  const syncAddressFormFromRecord = () => {
    const addr = effectiveRecord.address;

    if (!addr) {
      addressForm.resetFields();
      setSelectedStateId();
      setCepCityOption();
      return;
    }

    addressForm.setFieldsValue({
      postal_code: addr.postal_code,
      street: addr.street,
      address_number: addr.address_number,
      address_complement: addr.address_complement,
      neighborhood: addr.neighborhood,
      state_id: addr.city?.state_id,
      city_id: addr.city?.id
    });
    setSelectedStateId(addr.city?.state_id);

    if (addr.city) {
      setCepCityOption({ label: addr.city.name, value: addr.city.id });
    }
  };

  useEffect(() => {
    if (!addressEditing) {
      addressBaselinePayloadRef.current = null;
      return;
    }

    syncAddressFormFromRecord();
    addressBaselinePayloadRef.current = buildUserAddressPayload(
      addressForm.getFieldsValue(true),
      effectiveRecord.address
    );
    setAddressCompareTick(t => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressEditing]);

  void addressCompareTick;

  const addressDirty = Boolean(
    addressEditing &&
      addressBaselinePayloadRef.current &&
      !isEqual(
        buildUserAddressPayload(
          addressForm.getFieldsValue(true),
          effectiveRecord.address
        ),
        addressBaselinePayloadRef.current
      )
  );

  const startAddressEdit = () => {
    setAddressEditing(true);
    lastFetchedCep.current = '';
  };

  const cancelAddressEdit = () => {
    setAddressEditing(false);
    lastFetchedCep.current = '';
    addressForm.resetFields();
  };

  const handleAddressFinish = (values: Record<string, unknown>) => {
    const addressPayload = buildUserAddressPayload(
      values,
      effectiveRecord.address
    );

    if (!isAddressPayloadValid(addressPayload)) {
      message.error('Selecione uma cidade antes de salvar o endereço.');
      return;
    }

    saveAddress(
      {
        resource: 'users',
        id: record.id,
        values: {
          address_attributes: addressPayload
        },
        invalidates: ['list', 'many'],
        successNotification: {
          message: 'Endereço salvo',
          description: 'O endereço foi atualizado com sucesso.',
          type: 'success'
        }
      },
      {
        onSuccess: (data: { data?: IUser }) => {
          const addr = data?.data?.address;

          if (addr) {
            setAddressOverride(addr);
          }

          setAddressEditing(false);
          addressForm.resetFields();
        }
      }
    );
  };

  const addressFormItems = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={8}>
        <Form.Item
          label="CEP"
          name="postal_code"
          initialValue={effectiveRecord.address?.postal_code}
          rules={[cepRule(addressEditing)]}
        >
          <InputMask mask="00000-000" />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Rua"
          name="street"
          initialValue={effectiveRecord.address?.street}
          rules={[requiredAddressTextRule(addressEditing, 'Rua obrigatória')]}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Número"
          name="address_number"
          initialValue={effectiveRecord.address?.address_number}
          rules={[
            requiredAddressTextRule(addressEditing, 'Número obrigatório')
          ]}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Complemento"
          name="address_complement"
          initialValue={effectiveRecord.address?.address_complement}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Bairro"
          name="neighborhood"
          initialValue={effectiveRecord.address?.neighborhood}
          rules={[
            requiredAddressTextRule(addressEditing, 'Bairro obrigatório')
          ]}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Estado"
          name="state_id"
          initialValue={effectiveRecord.address?.city?.state_id}
        >
          <Select
            {...stateSelectProps}
            allowClear
            onChange={value => {
              setSelectedStateId(value as unknown as string);
              // eslint-disable-next-line unicorn/no-useless-undefined
              setPendingCityIbge(undefined);
              // eslint-disable-next-line unicorn/no-useless-undefined
              setCepCityOption(undefined);
              // eslint-disable-next-line unicorn/no-useless-undefined
              addressForm.setFieldValue('city_id', undefined);
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          label="Cidade"
          name="city_id"
          initialValue={effectiveRecord.address?.city?.id}
          rules={[requiredCityRule(addressEditing)]}
        >
          <Select
            {...citySelectProps}
            options={
              cepCityOption &&
              !citySelectProps.options?.find(
                o => o.value === cepCityOption.value
              )
                ? [...(citySelectProps.options ?? []), cepCityOption]
                : citySelectProps.options
            }
            allowClear
            disabled={!selectedStateId}
            // eslint-disable-next-line unicorn/no-useless-undefined
            onChange={() => setCepCityOption(undefined)}
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const readOnlyAddress = effectiveRecord.address ? (
    <Row gutter={[16, 24]}>
      {[
        { label: 'CEP', value: effectiveRecord.address.postal_code },
        { label: 'Rua', value: effectiveRecord.address.street },
        { label: 'Número', value: effectiveRecord.address.address_number },
        {
          label: 'Complemento',
          value: effectiveRecord.address.address_complement
        },
        { label: 'Bairro', value: effectiveRecord.address.neighborhood },
        {
          label: 'Estado',
          value: stateUfLabel ?? effectiveRecord.address.city?.state_id
        },
        { label: 'Cidade', value: effectiveRecord.address.city?.name }
      ].map(({ label, value }) => (
        <Col key={label} xs={24} sm={12} md={8}>
          <ShowFieldData label={label} value={value ?? '--'} />
        </Col>
      ))}
    </Row>
  ) : (
    <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
      Nenhum endereço cadastrado.
    </Typography.Paragraph>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" gutter={[16, 8]}>
        <Col>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Endereço
          </Typography.Title>
        </Col>
        <Col>
          {!addressEditing && (
            <Button type="primary" onClick={startAddressEdit}>
              {effectiveRecord.address
                ? 'Editar endereço'
                : 'Adicionar endereço'}
            </Button>
          )}
        </Col>
      </Row>

      {!addressEditing ? (
        readOnlyAddress
      ) : (
        <Form
          form={addressForm}
          layout="vertical"
          preserve={false}
          onFinish={handleAddressFinish}
          onValuesChange={() => setAddressCompareTick(t => t + 1)}
        >
          {addressFormItems}
          <Row justify="end">
            <Space>
              <Button onClick={cancelAddressEdit}>
                Cancelar edição do endereço
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={addressSaving || isCityFetching || !!pendingCityIbge}
                disabled={
                  addressSaving ||
                  !addressDirty ||
                  isCityFetching ||
                  !!pendingCityIbge
                }
              >
                Salvar endereço
              </Button>
            </Space>
          </Row>
        </Form>
      )}
    </Space>
  );
}
